import NextLink from 'next/link'
import React, { FC } from 'react'

import { Box, Button, CircularProgress } from '@material-ui/core'
import { BoxProps } from '@material-ui/core/Box'
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'

import Link from '~/components/shared/Link'
import Loader from '~/components/shared/Loader'
import Text from '~/components/shared/Text'

import { useMobile } from '~/hooks/responsive'

import SimpleCard from './SimpleCard'

const useStyles = makeStyles(
  (theme: Theme) =>
    createStyles({
      link: {
        '&:hover': {
          color: theme.palette.primary.main
        }
      },
      modalTitle: {
        [theme.breakpoints.down('sm')]: {
          padding: '14px'
        }
      },
      modalChevronIcon: {
        [theme.breakpoints.down('sm')]: {
          width: '1.1rem',
          height: '1.1rem'
        }
      },
      modalLink: {
        display: 'flex',
        width: '90px'
      }
    }),
  { name: 'SectionCard' }
)

export interface SectionCardProps extends Omit<BoxProps, 'title'> {
  title: string | JSX.Element
  titleSize?: number
  displayLink?: boolean
  linkHref?: string
  linkText?: string | JSX.Element
  empty?: boolean
  emptyLink?: string
  emptyText?: string
  emptyButtonText?: string
  loading?: boolean
  unmountOnEmpty?: boolean
  sync?: boolean
  linkLocation?: 'bottom' | 'top'
  ref?: React.RefObject<HTMLElement>
  toggleDialog?: () => void
  contactSellerDialogClose?: () => void
}

const SectionCard: FC<SectionCardProps> = ({
  children,
  title,
  titleSize,
  displayLink,
  linkText,
  linkHref,
  empty,
  emptyLink,
  emptyText,
  emptyButtonText,
  unmountOnEmpty,
  loading,
  linkLocation = 'bottom',
  sync,
  toggleDialog,
  contactSellerDialogClose,
  ...props
}) => {
  const isMobile = useMobile()
  const classes = useStyles()

  const hasLinkLocation = (location: SectionCardProps['linkLocation']) =>
    location === linkLocation

  if (unmountOnEmpty && empty) return null

  const handleClick = () => {
    if (contactSellerDialogClose) {
      contactSellerDialogClose()
    }
  }

  return (
    <SimpleCard p={0} {...props}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        p={2.5}
        className={hasLinkLocation('top') && classes.modalTitle}
      >
        <Box
          display="flex"
          width={`${isMobile && hasLinkLocation('top') ? '100%' : 'unset'}`}
          justifyContent="space-between"
          alignItems="center"
        >
          <Text
            variant="h3"
            fontWeight={500}
            fontFamily="Prompt, sans-serif"
            lineHeight="1.5"
            fontSize={titleSize || ''}
            mr={1}
          >
            {title}
          </Text>
          {hasLinkLocation('top') && !empty && displayLink && isMobile && (
            <Link href={linkHref}>
              {linkText || (
                <Text color="textPrimary">
                  <Box
                    component="span"
                    display="flex"
                    alignItems="center"
                    fontSize="14px"
                    onClick={handleClick}
                  >
                    View all{' '}
                    <ChevronRightIcon className={classes.modalChevronIcon} />
                  </Box>
                </Text>
              )}
            </Link>
          )}
          {sync && <CircularProgress size={26} />}
        </Box>

        {!empty && displayLink && !isMobile && (
          <Text fontWeight={400} fontFamily="Prompt, sans-serif" fontSize={14}>
            <Link href={linkHref} color="textPrimary" className={classes.link}>
              {linkText || (
                <Box
                  component="span"
                  display="inline-flex"
                  alignItems="center"
                  onClick={handleClick}
                >
                  View all <ChevronRightIcon />
                </Box>
              )}
            </Link>
          </Text>
        )}
      </Box>
      {loading ? (
        <Loader pb={2.5} />
      ) : empty ? (
        <Box p={2.5}>
          {emptyText && <Text mb={emptyButtonText ? 4 : 0}>{emptyText}</Text>}
          {emptyButtonText && emptyLink && (
            <NextLink href={emptyLink} passHref>
              <Button variant="contained" color="primary">
                {emptyButtonText}
              </Button>
            </NextLink>
          )}
        </Box>
      ) : (
        <>
          {children}
          {hasLinkLocation('bottom') && !empty && displayLink && isMobile && (
            <Box display="flex" justifyContent="center">
              <Link href={linkHref}>
                {linkText || (
                  <Text color="textPrimary">
                    <Box
                      component="span"
                      display="flex"
                      alignItems="center"
                      mb={4}
                      mt={2}
                    >
                      View all <ChevronRightIcon />
                    </Box>
                  </Text>
                )}
              </Link>
            </Box>
          )}
        </>
      )}
    </SimpleCard>
  )
}

export default SectionCard
